import { Panel } from "../../components/panels/Panel";
import { useEffect, useState } from "react";
import { useSocket } from "../../contexts/SocketContext";
import { useParams } from "react-router-dom";
import { DiorScanState } from "./dior-components/DiorScanState";
import {
  convertToSchemeHistory,
  isCartFinished,
  sortKanbanCarts
} from "../../services/live-interface/dior/dior-interface-service";
import { DiorKanbanCard } from "./dior-components/DiorKanbanCard";
import { DiorDigitalTwin } from "./dior-components/DiorDigitalTwin";
import { DiorLiveSetupPage } from "./dior-components/DiorLiveSetupPage";
import { ArrowsRightLeftIcon, Cog6ToothIcon } from "@heroicons/react/20/solid";
import { useGetData } from "../../services/api/api-tools";
import { Loader } from "../../components/loading/Loader";

const DiorScreen1 = ({ scanState, cartsList, processes }) => {
  const currentCarts = cartsList.filter((cart) => {
    const process = processes.find((process) => process.name === cart.rack_type)?.pattern_data?.[0]?.json;
    if (!process) return false;
    return !isCartFinished(convertToSchemeHistory(cart.history), process);
  });
  const sortedCurrentCarts = sortKanbanCarts(currentCarts, processes);

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex gap-4">
        <div className="flex-1">
          <DiorScanState scanState={scanState} />
        </div>
        <div className="w-1/8">
          <Panel title="NAVIGATION" height="full" className="rounded-md">
            <button className="text-white px-4 py-2 flex items-center bg-perception-blue rounded-md mb-4" onClick={() => window.location = '/interface/live/2'}>
              <ArrowsRightLeftIcon aria-hidden="true" className="size-5 mr-2" /> Switch screen
            </button>
            <button className="text-white px-4 py-2 flex items-center bg-perception-blue rounded-md" onClick={() => window.location = '/interface/setup'}>
              <Cog6ToothIcon aria-hidden="true" className="size-5 mr-2" /> Process setup
            </button>
          </Panel>
        </div>
      </div>
      <Panel title="ONGOING CARTS" height="full" className="rounded-md flex-1">
        <div className="grid grid-cols-3 gap-4 justify-start items-start">
          {
            sortedCurrentCarts.map((cart, cartIndex) =>
              <DiorKanbanCard key={cartIndex} cart={cart} showTimes={true} showErrors={false} processes={processes}/>
            )
          }
        </div>
      </Panel>
    </div>
  );
};

const DiorScreen2 = ({ scanState, cartsList, processes, positionMode='up', hasDoubt }) => {
  const currentCarts = cartsList.filter((cart) => {
    const process = processes.find((process) => process.name === cart.rack_type)?.pattern_data?.[0]?.json;
    if (!process) return false;
    return !isCartFinished(convertToSchemeHistory(cart.history), process)
  });
  const sortedCurrentCarts = sortKanbanCarts(currentCarts, processes);

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex gap-4">
        <Panel title="ONGOING CARTS" height="h-64" className="rounded-md flex-1">
          <div className="grid grid-cols-6 gap-4 justify-start items-start">
            {
              sortedCurrentCarts.map((cart, cartIndex) =>
                <DiorKanbanCard key={cartIndex} cart={cart} showTimes={false} showErrors={false} processes={processes} />
              )
            }
          </div>
        </Panel>
        <div className="w-1/8">
          <Panel title="NAVIGATION" height="full" className="rounded-md">
            <button className="text-white px-4 py-2 flex items-center bg-perception-blue rounded-md mb-4"
                    onClick={() => window.location = (positionMode === 'up' ? '/interface/live/3' : '/interface/live/1')}>
              <ArrowsRightLeftIcon aria-hidden="true" className="size-5 mr-2" /> Switch screen
            </button>
            <button className="text-white px-4 py-2 flex items-center bg-perception-blue rounded-md" onClick={() => window.location = '/interface/setup'}>
              <Cog6ToothIcon aria-hidden="true" className="size-5 mr-2" /> Process setup
            </button>
          </Panel>
        </div>
      </div>
      <div className="h-full flex-1">
        <DiorDigitalTwin cartsList={cartsList} scanState={scanState} positionMode={positionMode} hasDoubt={hasDoubt} processes={processes} />
      </div>
    </div>
  );
};

export const DiorLivePage = ({ isSetup=false }) => {

  const { screen } = useParams();
  const screenId = screen ? parseInt(screen) : 1;

  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();
  const [currentData, setCurrentData] = useState([]);
  const [cartsList, setCartsList] = useState([]);
  const [scanState, setScanState] = useState(0);
  const [hasDoubt, setHasDoubt] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleReceivedData = (data) => {
      console.log(data);
      if (data.process_names) return;
      if (data?.data !== undefined) setCartsList(data.data);
      if (data?.scan_state !== undefined) setScanState(data?.scan_state ?? 0);
      if (data?.doubt_detected !== undefined) setHasDoubt(data?.doubt_detected ?? false);
    };

    subscribeToEvent('current_data', handleReceivedData);

    return () => {
      unsubscribeFromEvent('current_data', handleReceivedData);
    };
  }, [socket]);

  if (isSetup) return <DiorLiveSetupPage/>

  const {
    data: patterns,
    isLoading: patternsAreLoading
  } = useGetData('patterns', `patterns`, {}, !!localStorage.getItem('token'));

  if (!patterns || patternsAreLoading) return <Loader/>;

  return (
    <Panel title="PRODUCTION LINE" category="main">
      <div className="flex flex-col w-full h-full gap-4 justify-center">
        <style>{`
          @keyframes blinkBorder {
            0%, 100% { border-color: var(--blink-border-color); }
            50% { border-color: transparent; }
          }
          .blink-border {
            animation: blinkBorder 1s ease-in-out infinite;
          }
        `}</style>

        {
          screenId === 2 ? <DiorScreen2 cartsList={cartsList} processes={patterns} hasDoubt={hasDoubt}/>
          : screenId === 3 ? <DiorScreen2 cartsList={cartsList} processes={patterns} positionMode="bottom" hasDoubt={hasDoubt}/>
          : <DiorScreen1 scanState={scanState} cartsList={cartsList} processes={patterns} />
        }
      </div>
    </Panel>
  );
};